import React from 'react'
import { Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import InsetSegmentedControl from '../atoms/InsetSegmentedControl'
import FormSelect from '../atoms/FormSelect'
import { FormFieldError, FormFieldLabel, FormTextInput, fieldBoxShadow } from '../atoms/FormTextInput'
import PlusSvg from '../other/svg/PlusSvg'
import {
  AdditionalServiceInput,
  additionalServiceBreakdown,
  additionalServiceGross,
  ServicePriceType
} from 'src/helpers/additionalServices'
import PriceBreakdownCard from './PriceBreakdownCard'
import { sellContractFieldClass, SellContractFormControls, ShowSellContractError } from './SellContractSegmentField'

interface SellContractServicesSectionProps {
  services: AdditionalServiceInput[]
  bikeGross: number
  currencySymbol: string
  form: SellContractFormControls
  showError: ShowSellContractError
}

const emptyService = (): AdditionalServiceInput => ({
  name: '',
  amount: '',
  price_type: 'gross',
  vat_option: '',
  delivery_cost: false
})

const serviceVatOptions = [
  { value: 'vat_23', labelKey: 'sell_contract.vat_23' },
  { value: 'vat_10', labelKey: 'sell_contract.vat_10' },
  { value: 'vat_5', labelKey: 'sell_contract.vat_5' }
] as const

const numericPricePattern = /^\d*[.,]?\d*$/

interface ServiceAmountFieldProps {
  amount: string
  priceType: ServicePriceType
  onAmountChange: (value: string) => void
  onPriceTypeChange: (value: ServicePriceType) => void
}

const ServiceAmountField = ({
  amount,
  priceType,
  onAmountChange,
  onPriceTypeChange
}: ServiceAmountFieldProps): JSX.Element => {
  const { t } = useTranslation()

  return (
    <div className={`flex-1 ${sellContractFieldClass}`}>
      <FormFieldLabel label={t('sell_contract.additional_service_amount')} />
      <div
        className="self-stretch h-11 pl-4 pr-1 py-1 bg-backgrounds-bg-field-component rounded-md inline-flex justify-center items-center gap-1.5 overflow-hidden"
        style={{ boxShadow: fieldBoxShadow(false) }}>
        <input
          type="text"
          inputMode="decimal"
          value={amount}
          placeholder="0.00"
          onChange={(event) => {
            const nextValue = event.target.value
            if (numericPricePattern.test(nextValue)) {
              onAmountChange(nextValue.replace(',', '.'))
            }
          }}
          className="flex-1 h-full bg-transparent outline-none text-foregrounds-fg-base text-sm font-semibold font-Montserrat leading-5"
        />
        <InsetSegmentedControl
          className="shrink-0"
          value={priceType}
          onChange={(value) => {
            onPriceTypeChange(value === 'net' ? 'net' : 'gross')
          }}
          options={[
            { value: 'gross', label: t('sell_contract.gross') },
            { value: 'net', label: t('sell_contract.net') }
          ]}
        />
      </div>
    </div>
  )
}

const SellContractServicesSection = ({
  services,
  bikeGross,
  currencySymbol,
  form,
  showError
}: SellContractServicesSectionProps): JSX.Element => {
  const { t } = useTranslation()
  const filledServices = services.filter(
    (service) => service.name.trim().length > 0 || service.amount.trim().length > 0
  )
  const servicesGross = filledServices.reduce((sum, service) => sum + additionalServiceGross(service), 0)
  const total = Math.round((bikeGross + servicesGross) * 100) / 100

  const update = (next: AdditionalServiceInput[]): void => {
    form.change('additional_services', next)
  }

  const patchService = (index: number, patch: Partial<AdditionalServiceInput>): void => {
    update(
      services.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item
        }
        return { ...item, ...patch }
      })
    )
  }

  return (
    <Field name="additional_services">
      {({ meta }) => (
        <div className="self-stretch flex flex-col justify-start items-start gap-4">
          <div className="text-zinc-900 text-xl font-bold font-Montserrat leading-7">
            {t('sell_contract.additional_services')}
          </div>
          {services.map((service, index) => {
            const breakdown = additionalServiceBreakdown(service)
            return (
              <div key={index} className="self-stretch flex flex-col justify-start items-start gap-4">
                <div className={`self-stretch ${sellContractFieldClass}`}>
                  <FormFieldLabel label={t('sell_contract.additional_service_name')} />
                  <FormTextInput
                    value={service.name}
                    onChange={(value) => {
                      patchService(index, { name: value })
                    }}
                    placeholder={t('sell_contract.additional_service_name')}
                  />
                </div>
                <div className="self-stretch inline-flex justify-start items-start gap-8">
                  <div className={`flex-1 ${sellContractFieldClass}`}>
                    <FormFieldLabel label={t('sell_contract.vat_rate')} />
                    <FormSelect
                      value={service.vat_option}
                      onChange={(value) => {
                        patchService(index, { vat_option: value })
                      }}
                      options={serviceVatOptions.map((option) => ({
                        value: option.value,
                        label: t(option.labelKey)
                      }))}
                      placeholder={t('sell_contract.select')}
                    />
                  </div>
                  <ServiceAmountField
                    amount={service.amount}
                    priceType={service.price_type}
                    onAmountChange={(value) => {
                      patchService(index, { amount: value })
                    }}
                    onPriceTypeChange={(value) => {
                      patchService(index, { price_type: value })
                    }}
                  />
                </div>
                <PriceBreakdownCard
                  netPrice={breakdown.netPrice}
                  vatAmount={breakdown.vatAmount}
                  vatRate={breakdown.vatRate}
                  grossPrice={breakdown.grossPrice}
                  currencySymbol={currencySymbol}
                />
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={service.delivery_cost}
                    onChange={(event) => {
                      const { checked } = event.target
                      update(
                        services.map((item, itemIndex) => ({
                          ...item,
                          delivery_cost: checked && itemIndex === index
                        }))
                      )
                    }}
                  />
                  <span className="text-zinc-900 text-sm font-medium font-Montserrat leading-5">
                    {t('sell_contract.additional_service_delivery_cost')}
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    update(services.filter((_, itemIndex) => itemIndex !== index))
                  }}
                  className="text-foregrounds-fg-muted text-sm font-semibold font-Montserrat leading-5">
                  {t('sell_contract.additional_service_remove')}
                </button>
              </div>
            )
          })}
          <button
            type="button"
            onClick={() => {
              update([...services, emptyService()])
            }}
            className="p-2 bg-backgrounds-bg-base rounded-lg inline-flex justify-start items-center gap-2">
            <PlusSvg />
            <span className="text-zinc-900 text-sm font-semibold font-Montserrat leading-5">
              {t('sell_contract.additional_service_add')}
            </span>
          </button>
          {filledServices.length > 0 ? (
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="text-foregrounds-fg-base text-sm font-semibold font-Montserrat leading-5">
                {t('sell_contract.additional_service_total')}
              </div>
              <div className="text-foregrounds-fg-base text-sm font-semibold font-Montserrat leading-5">
                {total.toFixed(2)} {currencySymbol}
              </div>
            </div>
          ) : null}
          <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
        </div>
      )}
    </Field>
  )
}

export default SellContractServicesSection
